import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import AuthLayout from '@/components/layout/AuthLayout';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { AuthProvider } from '@/contexts/AuthContext';
import { TenantProvider } from '@/contexts/TenantContext';
import { ProjectProvider } from '@/contexts/ProjectContext';
import { SipProvider } from '@/contexts/SipContext';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import PermissionRoute from '@/components/auth/PermissionRoute';

// Pages
import Dashboard from '@/pages/Dashboard';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import Login from '@/pages/auth/Login';
import Register from '@/pages/auth/Register';
import Leads from '@/pages/leads/Leads';
import LeadDetail from '@/pages/leads/LeadDetail';
import Contacts from '@/pages/contacts/Contacts';
import ContactDetail from '@/pages/contacts/ContactDetail';
import CompanyDetail from '@/pages/companies/CompanyDetail';
import Pipeline from '@/pages/pipeline/Pipeline';
import Projects from '@/pages/projects/Projects';
import ProjectDetail from '@/pages/projects/ProjectDetail';
import ProjectSettings from '@/pages/projects/ProjectSettings';
import Settings from '@/pages/settings/Settings';
import UserManagement from '@/pages/settings/UserManagement';
import Forms from '@/pages/forms/Forms';
import FormCreate from '@/pages/forms/FormCreate';
import FormDetail from '@/pages/forms/FormDetail';
import FormEditor from '@/pages/forms/FormEditor';
import FormPreview from '@/pages/forms/FormPreview';
import FormTheme from '@/pages/forms/FormTheme';
import { EmailTemplates } from '@/pages/email/EmailTemplates';
import { EmailSequences } from '@/pages/email/EmailSequences';
import { EmailTemplateEditor } from '@/pages/email/EmailTemplateEditor';
import Properties from '@/pages/properties/Properties';
import Mortgages from '@/pages/mortgages/Mortgages';
import Guides from '@/pages/guides/Guides';

// Call Center pages
import CallCenterDashboard from '@/pages/callcenter/CallCenterDashboard';
import CallScripts from '@/pages/callcenter/CallScripts';
import CallStats from '@/pages/callcenter/CallStats';
import CampaignList from '@/pages/callcenter/CampaignList';
import CampaignCreate from '@/pages/callcenter/CampaignCreate';
import CampaignDetail from '@/pages/callcenter/CampaignDetail';
import CampaignCallView from '@/pages/callcenter/CampaignCallView';

// Smart dashboard — renders CC dashboard for call center roles
import SmartDashboard from '@/pages/SmartDashboard';

export default function App() {
  return (
    <AuthProvider>
      <TenantProvider>
        <ProjectProvider>
          <SipProvider>
          <BrowserRouter>
          <Routes>
            {/* Auth routes */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
            </Route>

            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route element={<DashboardLayout />}>
                {/* Dashboard — auto-switches based on role */}
                <Route path="/" element={<SmartDashboard />} />

                {/* CRM routes — permission-guarded */}
                <Route path="/leads" element={<PermissionRoute permission="leads:all"><Leads /></PermissionRoute>} />
                <Route path="/leads/:id" element={<PermissionRoute permission="leads:all"><LeadDetail /></PermissionRoute>} />
                <Route path="/contacts" element={<PermissionRoute permission="contacts:all"><Contacts /></PermissionRoute>} />
                <Route path="/contacts/:id" element={<ContactDetail />} />
                <Route path="/companies/:id" element={<PermissionRoute permission="contacts:all"><CompanyDetail /></PermissionRoute>} />
                <Route path="/pipeline" element={<PermissionRoute permission="pipeline:read"><Pipeline /></PermissionRoute>} />
                <Route path="/properties" element={<PermissionRoute permission="properties"><Properties /></PermissionRoute>} />
                <Route path="/mortgages" element={<PermissionRoute permission="mortgages"><Mortgages /></PermissionRoute>} />
                <Route path="/projects" element={<PermissionRoute permission="projects:read"><Projects /></PermissionRoute>} />
                <Route path="/projects/:id" element={<PermissionRoute permission="projects:read"><ProjectDetail /></PermissionRoute>} />
                <Route path="/projects/:id/settings" element={<PermissionRoute permission="projects:manage"><ProjectSettings /></PermissionRoute>} />
                <Route path="/forms" element={<PermissionRoute permission="forms"><Forms /></PermissionRoute>} />
                <Route path="/forms/new" element={<PermissionRoute permission="forms"><FormCreate /></PermissionRoute>} />
                <Route path="/forms/:id" element={<PermissionRoute permission="forms"><FormDetail /></PermissionRoute>} />
                <Route path="/forms/:id/edit" element={<PermissionRoute permission="forms"><FormEditor /></PermissionRoute>} />
                <Route path="/forms/:id/theme" element={<PermissionRoute permission="forms"><FormTheme /></PermissionRoute>} />
                <Route path="/forms/:id/preview" element={<PermissionRoute permission="forms"><FormPreview /></PermissionRoute>} />
                <Route path="/email/templates" element={<PermissionRoute permission="email"><EmailTemplates /></PermissionRoute>} />
                <Route path="/email/templates/new" element={<PermissionRoute permission="email"><EmailTemplateEditor /></PermissionRoute>} />
                <Route path="/email/templates/:id" element={<PermissionRoute permission="email"><EmailTemplateEditor /></PermissionRoute>} />
                <Route path="/email/sequences" element={<PermissionRoute permission="email"><EmailSequences /></PermissionRoute>} />
                <Route path="/guides" element={<PermissionRoute permission="call_scripts:read"><Guides /></PermissionRoute>} />
                <Route path="/settings" element={<PermissionRoute permission="settings"><Settings /></PermissionRoute>} />
                <Route path="/settings/users" element={<PermissionRoute permission="users:manage"><UserManagement /></PermissionRoute>} />

                {/* Call Center routes */}
                <Route path="/callcenter/scripts" element={<PermissionRoute permission="call_scripts:read"><CallScripts /></PermissionRoute>} />
                <Route path="/callcenter/stats" element={<PermissionRoute permission="call_stats"><CallStats /></PermissionRoute>} />
                <Route path="/callcenter/campaigns" element={<PermissionRoute permission="campaigns:read"><CampaignList /></PermissionRoute>} />
                <Route path="/callcenter/campaigns/new" element={<PermissionRoute permission="campaigns:manage"><CampaignCreate /></PermissionRoute>} />
                <Route path="/callcenter/campaigns/:id" element={<PermissionRoute permission="campaigns:read"><CampaignDetail /></PermissionRoute>} />
                <Route path="/callcenter/campaigns/:id/call" element={<PermissionRoute permission="campaigns:operate"><CampaignCallView /></PermissionRoute>} />
              </Route>
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </BrowserRouter>
          </SipProvider>
        </ProjectProvider>
      </TenantProvider>
    </AuthProvider>
  );
}
